import {
    GoalDto,
    GoalFitnessMetricType,
    GoalRuleDto,
    GoalRuleType,
    GoalSocialMetricType,
    RecurrenceType,
    RewardConfigDto,
    RewardingType
} from '@api/FitnessActivityClient';
import { getLocalizedName } from '@core/utils/localized-name';
import {
    formatGoalActivityFilterLabel,
    goalActivityFiltersFromSource
} from './activity-filters';

const numberFormat = new Intl.NumberFormat('en-US');

export const todayIsoDate = (): string => {
    const now = new Date();
    return new Date(now.getTime() - now.getTimezoneOffset() * 60000)
        .toISOString()
        .slice(0, 10);
};

export const isGoalEnded = (goal: GoalDto, today = todayIsoDate()): boolean =>
    !!goal.dayTo && goal.dayTo < today;

export const recurrenceLabels: Record<RecurrenceType, string> = {
    [RecurrenceType.DAILY]: 'Daily',
    [RecurrenceType.WEEKLY]: 'Weekly',
    [RecurrenceType.MONTHLY]: 'Monthly',
    [RecurrenceType.LIFETIME]: 'One-off'
};

export const ruleTypeLabels: Record<GoalRuleType, string> = {
    [GoalRuleType.ACTIVITY_POINTS]: 'Activity points',
    [GoalRuleType.ACTIVITY_RAW_VALUE]: 'Raw value',
    [GoalRuleType.ACTIVITY_COUNT]: 'Activity count',
    [GoalRuleType.SOCIAL]: 'Social'
};

const rewardTiming: Record<RewardingType, string> = {
    [RewardingType.GOAL_REACHED]: 'on completion',
    [RewardingType.LEVEL_INCREASED]: 'per level',
    [RewardingType.STREAK_INCREASED]: 'per streak'
};

type Noun = [singular: string, plural: string];

const metricNouns: Record<GoalFitnessMetricType, Noun> = {
    [GoalFitnessMetricType.STEPS]: ['step', 'steps'],
    [GoalFitnessMetricType.DISTANCE]: ['distance', 'distance'],
    [GoalFitnessMetricType.CALORIES]: ['calorie', 'calories'],
    [GoalFitnessMetricType.ACTIVITY_TIME_IN_MINUTES]: ['minute', 'minutes']
};

const socialNouns: Record<GoalSocialMetricType, Noun> = {
    [GoalSocialMetricType.POSTS]: ['post', 'posts'],
    [GoalSocialMetricType.LIKES]: ['like', 'likes'],
    [GoalSocialMetricType.FOLLOWERS]: ['follower', 'followers']
};

const pluralize = (count: number, [singular, plural]: Noun) =>
    count === 1 ? singular : plural;

export const goalActivityScope = (rule: GoalRuleDto): string | undefined => {
    const filters = goalActivityFiltersFromSource(rule);
    if (filters.length === 0) {
        return undefined;
    }
    return filters.map(formatGoalActivityFilterLabel).join(' or ');
};

export const describeGoalTarget = (rule: GoalRuleDto): string => {
    const value = rule.value;
    const amount = numberFormat.format(value);

    switch (rule.type) {
        case GoalRuleType.ACTIVITY_POINTS:
            return `${amount} activity ${pluralize(value, ['point', 'points'])}`;
        case GoalRuleType.ACTIVITY_RAW_VALUE:
            return `${amount} ${pluralize(
                value,
                metricNouns[
                    rule.fitnessMetricType ?? GoalFitnessMetricType.STEPS
                ]
            )}`;
        case GoalRuleType.SOCIAL:
            return `${amount} ${pluralize(
                value,
                socialNouns[rule.socialMetricType ?? GoalSocialMetricType.POSTS]
            )}`;
        default: {
            const scope = goalActivityScope(rule);
            return scope
                ? `${amount}× ${scope}`
                : `${amount} ${pluralize(value, ['activity', 'activities'])}`;
        }
    }
};

export const describeGoal = (
    rule: GoalRuleDto,
    recurrence: RecurrenceType
): string => `${recurrenceLabels[recurrence]} · ${describeGoalTarget(rule)}`;

export const goalTitle = (goal: GoalDto): string =>
    goal.name?.trim() || describeGoal(goal.rule, goal.recurrence);

const rewardThreshold = (rewardConfig: RewardConfigDto): string | undefined => {
    const { minLevelValue = 1, minStreakValue = 2 } = rewardConfig;

    switch (rewardConfig.rewardingType) {
        case RewardingType.LEVEL_INCREASED:
            return minLevelValue > 1
                ? `from level ${minLevelValue}`
                : undefined;
        case RewardingType.STREAK_INCREASED:
            return minStreakValue > 2
                ? `from streak ${minStreakValue}`
                : undefined;
        default:
            return undefined;
    }
};

const rewardSubject = (
    rewardConfig: RewardConfigDto,
    badgeName: Record<string, string> | undefined
): string | undefined => {
    if (rewardConfig.badgeLevelId != null) {
        return badgeName ? getLocalizedName(badgeName) : 'Badge';
    }
    if (rewardConfig.value == null) {
        return undefined;
    }
    return [numberFormat.format(rewardConfig.value), rewardConfig.currencyCode]
        .filter(Boolean)
        .join(' ');
};

const rewardLimitPeriodLabels: Record<RecurrenceType, string> = {
    [RecurrenceType.DAILY]: 'per day',
    [RecurrenceType.WEEKLY]: 'per week',
    [RecurrenceType.MONTHLY]: 'per month',
    [RecurrenceType.LIFETIME]: 'ever'
};

const rewardLimit = (rewardConfig: RewardConfigDto): string | undefined => {
    const limit = rewardConfig.limit;
    if (!limit) {
        return undefined;
    }
    return `max ${numberFormat.format(limit.maxRewards)} ${
        rewardLimitPeriodLabels[limit.maxRewardsPeriod]
    }`;
};

export const describeReward = (
    rewardConfig: RewardConfigDto,
    badgeName = rewardConfig.badgeLevel?.name
): string | undefined => {
    const subject = rewardSubject(rewardConfig, badgeName);
    if (!subject) {
        return undefined;
    }

    const threshold = rewardThreshold(rewardConfig);
    const description = [
        subject,
        rewardTiming[rewardConfig.rewardingType],
        threshold && `(${threshold})`
    ]
        .filter(Boolean)
        .join(' ');

    return [description, rewardLimit(rewardConfig)].filter(Boolean).join(' · ');
};
